// <s-banner-glow> — tints decorative glows with the colours of the slideshow banner on screen.
// On each 'slideshow:change' from the slideshow whose id is in data-source, it reads the active
// slide's image, samples three regions (left, centre, right) weighted towards colourful pixels,
// and sets --glow-1/2/3. Works automatically for any banner image added in the future.
// data-palettes (optional JSON) is only a fallback if an image can't be read.
// The colour fade is CSS (transition on the glow elements), so reduced motion just swaps.
class SBannerGlow extends HTMLElement {
  #ac = null;
  #fallback = [];
  #cache = new Map();
  #request = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    try {
      this.#fallback = JSON.parse(this.dataset.palettes || "[]");
    } catch {
      this.#fallback = [];
    }

    document.addEventListener(
      "slideshow:change",
      (event) => {
        if (this.dataset.source && event.target?.id !== this.dataset.source) return;
        this.#update(event.target, event.detail?.index ?? 0);
      },
      { signal: this.#ac.signal },
    );

    const source = this.dataset.source && document.getElementById(this.dataset.source);
    if (source) this.#update(source, 0);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  async #update(slideshow, index) {
    const request = ++this.#request;
    const slides = slideshow.querySelectorAll("[data-slide]");
    const img = slides[index]?.querySelector("img");
    let palette = null;

    if (img) {
      try {
        palette = await this.#paletteFor(img);
      } catch {
        palette = null;
      }
    }
    if (!palette) palette = this.#fallback[index % (this.#fallback.length || 1)];
    if (request !== this.#request || !Array.isArray(palette)) return;

    palette.forEach((color, i) => this.style.setProperty(`--glow-${i + 1}`, color));
  }

  async #paletteFor(img) {
    const src = img.currentSrc || img.src;
    if (!src) return null;
    if (this.#cache.has(src)) return this.#cache.get(src);

    // Load a small copy so sampling is cheap; Shopify image URLs accept a width param.
    const url = new URL(src, location.href);
    url.searchParams.set("width", "120");
    const probe = new Image();
    probe.crossOrigin = "anonymous";
    probe.decoding = "async";
    probe.src = url.href;
    await probe.decode();

    const w = 48;
    const h = Math.max(1, Math.round((probe.naturalHeight / probe.naturalWidth) * w));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    ctx.drawImage(probe, 0, 0, w, h);
    const { data } = ctx.getImageData(0, 0, w, h);

    const third = w / 3;
    const palette = [0, 1, 2].map((region) => {
      let r = 0, g = 0, b = 0, total = 0;
      for (let y = 0; y < h; y++) {
        for (let x = Math.floor(region * third); x < Math.floor((region + 1) * third); x++) {
          const i = (y * w + x) * 4;
          const pr = data[i], pg = data[i + 1], pb = data[i + 2];
          const max = Math.max(pr, pg, pb), min = Math.min(pr, pg, pb);
          // Weight colourful pixels far above greys/whites so the brand colour wins.
          const weight = 0.05 + ((max - min) / 255) ** 2;
          r += pr * weight; g += pg * weight; b += pb * weight; total += weight;
        }
      }
      const toHex = (v) => Math.round(v / total).toString(16).padStart(2, "0");
      return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
    });

    this.#cache.set(src, palette);
    return palette;
  }
}

if (!customElements.get("s-banner-glow")) {
  customElements.define("s-banner-glow", SBannerGlow);
}
